import { router, Route } from './router.js';
import { HomePage } from './pages/home.js';
import { ContainerPage, ContainerPageConfig } from './pages/container.js';
import { PDFViewerPage, PDFViewerPageConfig } from './pages/pdf-viewer-page.js';
import { showStatusNotification } from './utils.js';

export class App {
  private currentPage: HomePage | ContainerPage | PDFViewerPage | null = null;
  private appContainer: HTMLElement;

  constructor() {
    this.appContainer = this.createAppContainer();
    this.setupRouting();
    this.setupGlobalStyles();
    // Don't start router here - wait for mount() to set the proper container
  }

  private createAppContainer(): HTMLElement {
    // Instead of creating a new container, we'll work with the existing HTML structure
    // This method is kept for compatibility but will be handled differently in mount()
    const container = document.createElement('div');
    container.className = 'app-content';
    return container;
  }

  private setupRouting(): void {
    // Home page route
    router.on('/', () => {
      this.showHomePage();
    });

    // Container page routes - using unified design system
    const containerConfigs: ContainerPageConfig[] = [
      {
        containerId: 'pdf1',
        containerName: 'Blechstrasse 1',
        containerColor: 'var(--primary-color)'
      },
      {
        containerId: 'pdf2',
        containerName: 'Blechstrasse 2',
        containerColor: 'var(--primary-color)'
      },
      {
        containerId: 'pdf3',
        containerName: 'Blechstrasse 3',
        containerColor: 'var(--primary-color)'
      },
      {
        containerId: 'pdf4',
        containerName: 'Blechstrasse 4',
        containerColor: 'var(--primary-color)'
      },
      {
        containerId: 'pdf5',
        containerName: 'Biegezentrum Schröder',
        containerColor: 'var(--primary-color)'
      },
      {
        containerId: 'pdf6',
        containerName: 'Türenpressen',
        containerColor: 'var(--primary-color)'
      },
      {
        containerId: 'pdf7',
        containerName: 'Zuschnitte',
        containerColor: 'var(--primary-color)'
      }
    ];

    containerConfigs.forEach(config => {
      router.on(`/${config.containerId}` as Route, () => {
        this.showContainerPage(config);
      });
      
      // Add PDF viewer route for each container
      router.on(`/${config.containerId}/view` as Route, () => {
        this.showPDFViewerPage(config.containerId, config.containerName);
      });
    });
  }

  private setupGlobalStyles(): void {
    // Add global CSS animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .fade-in {
        animation: fadeIn 0.3s ease-out;
      }
      
      /* Touch-friendly button styles */
      button, input, select {
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
      }
      
      /* iPad-specific optimizations */
      @media (max-width: 1024px) {
        .container-tile {
          min-height: 250px !important;
        }
        
        .container-tile h2 {
          font-size: 1.8rem !important;
        }
        
        .container-tile p {
          font-size: 1.1rem !important;
        }
      }
      
      /* Ensure minimum touch target size */
      button, input, select, .container-tile {
        min-height: 44px;
        min-width: 44px;
      }
    `;
    document.head.appendChild(style);
  }

  private async showHomePage(): Promise<void> {
    try {
      // Clean up current page
      if (this.currentPage) {
        this.currentPage.destroy();
        this.currentPage = null;
      }

      // Create and show home page
      this.currentPage = new HomePage();
      this.currentPage.mount(this.appContainer);
      
      // Add fade-in animation to the main content
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.classList.add('fade-in');
      }
      
      console.log('Home page loaded');
      
    } catch (error) {
      console.error('Failed to load home page:', error);
      showStatusNotification('Failed to load home page. Please refresh.', 'error', 5000);
    }
  }

  private async showContainerPage(config: ContainerPageConfig): Promise<void> {
    try {
      // Clean up current page
      if (this.currentPage) {
        this.currentPage.destroy();
        this.currentPage = null;
      }

      // Create and show container page
      this.currentPage = new ContainerPage(config);
      this.currentPage.mount(this.appContainer);
      
      // Add fade-in animation
      this.appContainer.classList.add('fade-in');
      
      console.log(`Container page loaded: ${config.containerName}`);
      
    } catch (error) {
      console.error('Failed to load container page:', error);
      showStatusNotification(`Failed to load ${config.containerName}. Please try again.`, 'error', 5000);
      
      // Fallback to home page on error
      router.navigate('/');
    }
  }

  private async showPDFViewerPage(containerId: string, containerName: string): Promise<void> {
    try {
      // Extract the filename from URL parameters
      const fileName = router.getQueryParams().get('file');

      if (!fileName) {
        console.error('No filename found in URL parameters');
        showStatusNotification('Could not load PDF. Please try again.', 'error', 3000);
        // Navigate back to container page
        router.navigate(`/${containerId}` as Route);
        return;
      }

      // Clean up current page
      if (this.currentPage) {
        this.currentPage.destroy();
        this.currentPage = null;
      }

      // Create PDF viewer page config
      const config: PDFViewerPageConfig = {
        containerId: containerId,
        containerName: containerName,
        fileName: fileName,
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api'
      };

      // Create and show PDF viewer page
      this.currentPage = new PDFViewerPage(config);
      await this.currentPage.mount(this.appContainer);

      console.log(`PDF viewer page loaded: ${fileName}`);

    } catch (error) {
      console.error('Failed to load PDF viewer page:', error);
      showStatusNotification('Failed to load PDF viewer. Please try again.', 'error', 5000);

      // Fallback to container page on error
      router.navigate(`/${containerId}` as Route);
    }
  }

  public mount(target: HTMLElement): void {
    // Don't replace the entire target, work with the existing HTML structure
    // The target should be the #app element which already contains the header
    this.appContainer = target; // Use the existing app container
    
    // Start router after the container is properly set
    router.start();
  }


  public destroy(): void {
    if (this.currentPage) {
      this.currentPage.destroy();
      this.currentPage = null;
    }
    this.appContainer.remove();
  }

  // Add missing methods for compatibility
  public refresh(): void {
    // Refresh current page if it supports it
    if (this.currentPage && 'refresh' in this.currentPage) {
      (this.currentPage as any).refresh();
    }
  }

  public getCurrentFilters(): any {
    // Return current filters if current page supports it
    if (this.currentPage && 'getCurrentFilters' in this.currentPage) {
      return (this.currentPage as any).getCurrentFilters();
    }
    return {};
  }
}
